import { create } from 'zustand';
import { uniq, uniqBy } from 'lodash';
import moment from '../lib/moment';
import {
  COUPON_PRODUCT_ITEM_LIST_FILTER_OPTION_MODEL,
  COUPON_PRODUCT_ITEM_LIST_SORT_OPTION_MODEL,
  COUPON_BRAND_ITEM_LIST_FILTER_OPTION_MODEL,
} from './modules/coupon/models';
import { _LIST_PAGINATION_MODEL } from './modules/app/models';
import { setUncheckableTableTransferList } from '../lib/common';

/**
 * 레거시 COUPON_CREATE_MODEL과 동일한 초기 상태
 */
const createInitialState = () => ({
  currentTabKey: 0 as string | number,
  couponInfo: {
    couponId: null as number | null,
    name: null as string | null,
    availableType: 0,
    code: null as string | null,
    periodType: 0,
    discountType: 0,
    discountAmount: 10,
    minimumPurchasePrice: 9000,
    maxDiscountPrice: 2000,
    exclude24k: false,
    description: null as string | null,
    period: {
      startDate: moment(),
      endDate: moment().add(10, 'day'),
    },
    dayPlus: 0,
    memo: null as string | null,
    generateLimitType: 0,
    generateLimitCount: 1 as number | null,
    generateLimitDay: 1 as number | null,
    status: 1,
    isDownloadable: 0,
    settlementShare: {
      shareType: 'amount' as string,
      shareAmount: 0,
    },
  },
  couponProductLinkInfo: {
    itemList: {
      list: [] as Record<string, unknown>[],
      filterOption: { ...COUPON_PRODUCT_ITEM_LIST_FILTER_OPTION_MODEL },
      sortOption: { ...COUPON_PRODUCT_ITEM_LIST_SORT_OPTION_MODEL },
      pagination: { ..._LIST_PAGINATION_MODEL },
    },
    selectedItemList: {
      list: [] as Record<string, unknown>[],
      idList: [] as number[],
    },
    selectedItemBrandCount: 0,
  },
  couponBrandLinkInfo: {
    itemList: {
      list: [] as Record<string, unknown>[],
      filterOption: { ...COUPON_BRAND_ITEM_LIST_FILTER_OPTION_MODEL },
      pagination: { ..._LIST_PAGINATION_MODEL },
    },
    selectedItemList: {
      list: [] as Record<string, unknown>[],
      idList: [] as number[],
    },
  },
  mainProductCategorySelectList: [] as Record<string, unknown>[],
  subProductCategorySelectList: { null: [] } as Record<string, unknown>,
});

type CouponFormState = ReturnType<typeof createInitialState>;

/**
 * 중첩 경로에 값을 설정하는 유틸리티
 * 레거시 setData({ path: ['couponCreate', 'couponInfo', 'name'], value: 'test' }) 패턴 호환
 */
const setNestedValue = (obj: any, path: string[], value: unknown): any => {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const current = obj[head];
  return {
    ...obj,
    [head]: rest.length === 0 ? value : setNestedValue(current ?? {}, rest, value),
  };
};

type CouponFormStore = CouponFormState & {
  /** coupon-v2 페이지에서 활성화된 상태인지 여부 */
  _active: boolean;
  /** 레거시 setData 호환 - path[0]이 'couponCreate'면 자동 제거 */
  setData: (payload: { path: string[]; value: unknown }) => void;
  /** 전체 상태를 특정 값으로 설정 (edit/copy 로딩 시) */
  setState: (state: Partial<CouponFormState>) => void;
  /** 초기 상태로 리셋 */
  reset: () => void;
};

export const useCouponFormStore = create<CouponFormStore>((set) => ({
  ...createInitialState(),
  _active: false,

  setData: ({ path, value }) => {
    const adjustedPath = path[0] === 'couponCreate' ? path.slice(1) : path;
    if (adjustedPath.length === 0) return;
    set((state) => setNestedValue(state, adjustedPath, value));
  },

  setState: (partial) => set(partial),

  reset: () => set({ ...createInitialState(), _active: false }),
}));

/**
 * 엑셀 업로드된 브랜드 목록을 Zustand 스토어에 동기화
 * coupon-v2 흐름에서만 실행 (_active 가드 포함)
 */
export const syncBrandItemsToZustand = (brandList: Record<string, unknown>[]) => {
  const state = useCouponFormStore.getState();
  if (!state._active) return;

  const deduped = uniqBy(brandList, 'id');
  const idList = deduped.map((item) => item.id as number);
  const updatedItemList = setUncheckableTableTransferList({
    list: state.couponBrandLinkInfo.itemList.list,
    selectedIdList: idList,
  });
  useCouponFormStore.setState({
    couponBrandLinkInfo: {
      ...state.couponBrandLinkInfo,
      itemList: { ...state.couponBrandLinkInfo.itemList, list: updatedItemList },
      selectedItemList: { list: deduped, idList },
    },
  });
};

/**
 * 엑셀 업로드된 상품 목록을 Zustand 스토어에 동기화
 * coupon-v2 흐름에서만 실행 (_active 가드 포함)
 */
export const syncProductItemsToZustand = (productList: Record<string, unknown>[]) => {
  const state = useCouponFormStore.getState();
  if (!state._active) return;

  const deduped = uniqBy(productList, 'id');
  const idList = deduped.map((item) => item.id as number);
  const updatedItemList = setUncheckableTableTransferList({
    list: state.couponProductLinkInfo.itemList.list,
    selectedIdList: idList,
  });
  const storeIdList = deduped.map((item) => item.storeId as number);
  useCouponFormStore.setState({
    couponProductLinkInfo: {
      ...state.couponProductLinkInfo,
      itemList: { ...state.couponProductLinkInfo.itemList, list: updatedItemList },
      selectedItemList: { list: deduped, idList },
      selectedItemBrandCount: uniq(storeIdList).length,
    },
  });
};
